import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Baby, Smile, KeyRound, Heart } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import { trackPlanVisitClick } from "@/lib/analytics";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const PARENT_FAQS = [
  { q: "What ages does VC Kids serve?", a: "Nursery (babies through age 2) and Children's Church (age 3 through elementary). Older students are welcome in the main service as we grow our student ministry." },
  { q: "How do I check my child in?", a: "On your first visit, our team will help you register at the VC Kids check-in station in the lobby. You'll receive a matching security tag - only the adult with that tag can pick up your child. Plan your visit online and we'll have your family pre-registered." },
  { q: "Is VC Kids safe?", a: "Safety is our first priority. Every VC Kids volunteer completes a background check and training. Rooms are secure, clean, and supervised with two-adult minimums at all times." },
  { q: "What will my child learn?", a: "Age-appropriate, Bible-based lessons with music, crafts, games and small-group time. We want kids to leave excited to come back - and able to tell you what they learned about Jesus." },
  { q: "Can my child stay with me in the service?", a: "Absolutely. Kids are always welcome in the main service. VC Kids is a gift, never a requirement." },
  { q: "What if my child needs me during service?", a: "We'll contact you right away using the phone number from check-in. You're never more than a text away." },
];

const EXPERIENCE = [
  { icon: Heart, title: "KNOWN & LOVED", text: "Leaders who learn your child's name, their story, and what makes them light up." },
  { icon: Smile, title: "FUN ON PURPOSE", text: "Music, games, crafts and laughter - church should be the highlight of a kid's week." },
  { icon: ShieldCheck, title: "SAFE, ALWAYS", text: "Background-checked volunteers, secure rooms, and check-in tags that keep your family protected." },
  { icon: Baby, title: "JESUS AT THEIR LEVEL", text: "Bible teaching crafted for little hearts, from the nursery through elementary." },
];

export default function Kids() {
  return (
    <>
      <SEO
        title="VC Kids - Nursery & Children's Church in Clay County"
        description="VC Kids at Victory Chapel is a safe, fun, Bible-centered environment for nursery and elementary-age children in Clay County, FL. Background-checked volunteers, secure check-in, and lessons kids love."
        path="/kids"
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-victory/20 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <img src="/vc-kids-logo-512.png" alt="VC Kids logo - Know, Grow, Go" width="128" height="128"
               className="h-28 w-28 sm:h-32 sm:w-32" data-testid="kids-subbrand" />
          <LineReveal className="mt-4 font-display text-5xl font-900 tracking-tighter sm:text-7xl" lines={["YOUR KIDS WILL", "LOVE IT HERE."]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              We want children to experience church as a place where they are known, loved, safe
              and excited to learn about Jesus.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="kids-environments-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-800 tracking-tight text-navy sm:text-4xl text-balance">
              TWO ENVIRONMENTS, ONE GOAL: KIDS WHO LOVE JESUS.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-navy/10 bg-cream p-8 sm:p-10">
                <img src="/vc-kids-nursery.jpg" alt="Toddlers playing with colorful blocks in the VC Kids nursery"
                     width="1254" height="1254" loading="lazy" data-testid="nursery-photo"
                     className="aspect-[16/9] w-full rounded-2xl object-cover" />
                <h3 className="mt-6 font-display text-2xl font-800 tracking-tight text-navy">NURSERY</h3>
                <p className="mt-3 leading-relaxed text-slate-600">
                  Babies through age 2 are cared for in a calm, clean, loving nursery by trained volunteers -
                  so you can worship with complete peace of mind.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-navy/10 bg-cream p-8 sm:p-10">
                <img src="/vc-kids-childrens-church.jpg" alt="Elementary-age kids raising their hands during children's church"
                     width="1536" height="1024" loading="lazy" data-testid="childrens-church-photo"
                     className="aspect-[16/9] w-full rounded-2xl object-cover" />
                <h3 className="mt-6 font-display text-2xl font-800 tracking-tight text-navy">CHILDREN'S CHURCH</h3>
                <p className="mt-3 leading-relaxed text-slate-600">
                  Ages 3 through elementary experience high-energy worship, Bible stories, games and small groups
                  designed just for them.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-28" data-testid="kids-experience-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-800 tracking-tight text-navy sm:text-4xl">
              WHAT YOUR CHILD WILL EXPERIENCE
            </h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {EXPERIENCE.map((e) => (
              <StaggerItem key={e.title}>
                <div className="h-full rounded-3xl border border-navy/10 bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                  <e.icon size={26} className="text-victory" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-lg font-800 tracking-tight text-navy">{e.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{e.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-navy py-20 text-white sm:py-28" data-testid="kids-safety-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="font-display text-sm font-700 tracking-[0.25em] text-rose-300">SAFETY &amp; CHECK-IN</p>
              <h2 className="mt-4 font-display text-4xl font-800 tracking-tight sm:text-5xl text-balance">
                YOUR PEACE OF MIND IS NON-NEGOTIABLE.
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <ul className="mt-8 space-y-5 text-white/80">
                <li className="flex gap-4"><ShieldCheck size={22} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" /> Every volunteer is background-checked and trained before serving with kids.</li>
                <li className="flex gap-4"><KeyRound size={22} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" /> Secure check-in with matching tags - only you can pick up your child.</li>
                <li className="flex gap-4"><Baby size={22} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" /> First time? Arrive 15 minutes early and our team will walk you through everything.</li>
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-white/5 p-8 backdrop-blur-sm sm:p-10">
              <h3 className="font-display text-2xl font-800 tracking-tight">PARENT FAQ</h3>
              <Accordion type="single" collapsible className="mt-6" data-testid="kids-faq-accordion">
                {PARENT_FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`kf-${i}`} className="border-white/15">
                    <AccordionTrigger data-testid={`kids-faq-trigger-${i}`} className="text-left font-display text-base font-700 text-white hover:text-victory">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-white/70">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 text-center sm:py-24" data-testid="kids-cta-section">
        <div className="mx-auto max-w-3xl px-5">
          <Reveal>
            <h2 className="font-display text-4xl font-800 tracking-tight text-navy sm:text-5xl text-balance">
              BRING THE WHOLE FAMILY.
            </h2>
            <p className="mt-5 text-lg text-slate-600">Plan your visit and we'll pre-register your kids for a fast, easy first Sunday.</p>
            <Link to="/im-new" onClick={trackPlanVisitClick} data-testid="kids-plan-visit-cta"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-victory px-9 py-4 font-display text-base font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark hover:shadow-lift">
              PLAN YOUR FAMILY'S VISIT <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
